import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, Clock, Sunrise, Sun, CarFront, Moon, Bot, Siren, AudioLines, ArrowUpRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { produceInNewsStudio } from "@/components/newsroom/produce";

const CATS = ["music", "entertainment", "local", "sports", "weather", "traffic"];
const SLOTS = [
  { id: "top_of_hour", label: "Top Of Hour", time: "Every hour at :00", icon: Clock, format: "top_of_hour" },
  { id: "morning", label: "Morning Update", time: "6:00 AM daily", icon: Sunrise, format: "f60" },
  { id: "midday", label: "Midday Update", time: "12:00 PM daily", icon: Sun, format: "f60" },
  { id: "afternoon", label: "Afternoon Drive", time: "3:00 PM daily", icon: CarFront, format: "f60" },
  { id: "evening", label: "Evening Update", time: "6:00 PM daily", icon: Moon, format: "f60" },
];

export const ScheduleTab = ({ stationId, onScript }) => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [voices, setVoices] = useState([]);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState("");

  const loadRuns = useCallback(() => {
    api.get("/newsroom/autopilot/runs", { params: { station_id: stationId || "" } })
      .then((r) => setRuns(r.data || [])).catch(() => {});
  }, [stationId]);

  useEffect(() => {
    api.get("/newsroom/schedule", { params: { station_id: stationId || "" } })
      .then((r) => setCfg(r.data)).catch(() => {});
    api.get("/newsroom/anchor/voices").then((r) => setVoices(r.data || [])).catch(() => {});
    loadRuns();
  }, [stationId, loadRuns]);

  const save = async (payload) => {
    try {
      const r = await api.put("/newsroom/schedule", { station_id: stationId, ...payload });
      setCfg(r.data);
    } catch { toast.error("Could not save schedule"); }
  };

  const generate = async (slot) => {
    setBusy(slot.id);
    try {
      const cat = (cfg?.categories || [])[0] || "music";
      const r = await api.post("/newsroom/anchor/format-script", {
        format_id: slot.format, category: cat, station_id: stationId });
      toast.success(`${slot.label} bulletin written — opening Anchor Studio`);
      onScript(r.data.script);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(""); }
  };

  if (!cfg) return <div className="py-16 text-center text-sm text-ink-muted">Loading schedule…</div>;
  const enabledCount = Object.values(cfg.slots || {}).filter(Boolean).length;

  return (
    <div data-testid="newsroom-schedule-tab">
      <h2 className="font-display text-xl font-bold text-ink-primary">News Scheduler &amp; Autopilot</h2>
      <p className="text-xs text-ink-muted mb-6">Enabled slots generate and voice bulletins automatically at their local times — hands-free</p>

      <div className={`rounded-xl border p-5 mb-5 flex flex-wrap items-center gap-4 ${enabledCount ? "border-emerald-500/40 bg-emerald-500/5" : "border-line bg-bg-surface"}`} data-testid="autopilot-status">
        <div className={`rounded-lg p-2.5 ${enabledCount ? "bg-emerald-500/15" : "bg-bg-elevated"}`}>
          <Bot className={`h-5 w-5 ${enabledCount ? "text-emerald-400" : "text-ink-muted"}`} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-bold text-ink-primary">
            Bulletin Autopilot {enabledCount ? <span className="text-emerald-400">· ARMED</span> : <span className="text-ink-muted">· idle</span>}
          </div>
          <div className="text-xs text-ink-muted mt-0.5">
            {enabledCount
              ? `${enabledCount} slot${enabledCount === 1 ? "" : "s"} enabled — bulletins are written from your latest real stories and voiced automatically`
              : "Enable a slot below and the newsroom writes + voices the bulletin for you at that time"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Autopilot Voice</span>
          <select value={cfg.auto_voice || "female_news"} onChange={(e) => save({ auto_voice: e.target.value })} data-testid="autopilot-voice-select"
            className="rounded-lg border border-line bg-bg-surface px-3 py-2 text-xs text-ink-primary focus:outline-none focus:border-brand-amber/50">
            {voices.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-5 flex flex-wrap items-center gap-4" data-testid="breaking-alerts-row">
        <div className="rounded-lg bg-rose-500/15 p-2.5"><Siren className="h-5 w-5 text-rose-400" /></div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-bold text-ink-primary">Breaking Email Alerts</div>
          <div className="text-xs text-ink-muted mt-0.5">Email you the moment an urgent story (deaths, arrests, emergencies, cancellations…) lands in your feeds</div>
        </div>
        <Switch checked={cfg.breaking_alerts !== false} onCheckedChange={(v) => save({ breaking_alerts: v })} data-testid="breaking-alerts-switch" />
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-5 flex flex-wrap items-center gap-4" data-testid="schedule-publishing-mode">
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-semibold text-ink-primary">Publishing Mode</div>
          <div className="text-xs text-ink-muted mt-0.5">
            {cfg.publishing_mode === "auto" ? "New stories publish automatically" : "All stories require manual approval before publishing"}
          </div>
        </div>
        <div className="flex gap-2">
          {[["manual", "Manual Approval"], ["auto", "Auto Publish"]].map(([id, label]) => (
            <button key={id} onClick={() => save({ publishing_mode: id })} data-testid={`schedule-mode-${id}`}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition ${cfg.publishing_mode === id ? "bg-brand-amber text-black" : "border border-line text-ink-secondary hover:text-ink-primary"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-6" data-testid="schedule-categories">
        <div className="text-sm font-semibold text-ink-primary mb-3">News Categories to Include</div>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => {
            const on = (cfg.categories || []).includes(c);
            return (
              <button key={c} data-testid={`schedule-cat-${c}`}
                onClick={() => save({ categories: on ? cfg.categories.filter((x) => x !== c) : [...(cfg.categories || []), c] })}
                className={`rounded-full px-3.5 py-1.5 text-xs capitalize transition ${on ? "bg-violet-500/20 border border-violet-400/50 text-violet-200 font-semibold" : "border border-line text-ink-secondary hover:text-ink-primary"}`}>
                {c === "music" ? "Music News" : c}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-ink-muted mt-2">Autopilot rotates bulletin topics through these categories.</p>
      </div>

      <div className="text-sm font-semibold text-ink-primary mb-3">Bulletin Schedule</div>
      <div className="space-y-3 mb-8">
        {SLOTS.map((slot) => {
          const on = !!cfg.slots?.[slot.id];
          return (
            <div key={slot.id} className={`flex items-center gap-4 rounded-xl border p-4 ${on ? "border-emerald-500/40 bg-emerald-500/5" : "border-line bg-bg-surface"}`} data-testid={`schedule-slot-${slot.id}`}>
              <button onClick={() => save({ slots: { ...cfg.slots, [slot.id]: !on } })} data-testid={`schedule-slot-toggle-${slot.id}`}
                className={`h-5 w-5 rounded-full border-2 transition shrink-0 ${on ? "border-emerald-400 bg-emerald-400" : "border-ink-muted"}`} />
              <div className="rounded-lg bg-bg-elevated p-2.5"><slot.icon className="h-4 w-4 text-brand-amber" /></div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-ink-primary">{slot.label} {on && <span className="text-[9px] font-mono uppercase text-emerald-400 ml-1">auto</span>}</div>
                <div className="text-[11px] text-ink-muted flex items-center gap-1"><Clock className="h-3 w-3" /> {slot.time}</div>
              </div>
              <button onClick={() => generate(slot)} disabled={!!busy} data-testid={`schedule-slot-generate-${slot.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3.5 py-2 text-xs font-semibold text-ink-primary hover:border-brand-amber/50 transition disabled:opacity-50">
                {busy === slot.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-brand-amber" />}
                Generate now
              </button>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="autopilot-log">
        <div className="flex items-center gap-2 mb-3">
          <AudioLines className="h-4 w-4 text-emerald-400" />
          <span className="text-sm font-bold text-ink-primary">Autopilot Log</span>
          <button onClick={loadRuns} className="ml-auto text-[11px] text-ink-muted hover:text-ink-primary transition">Refresh</button>
        </div>
        {runs.length === 0 ? (
          <div className="text-xs text-ink-muted py-4 text-center">No autopilot runs yet — enable a slot above and the log fills in automatically.</div>
        ) : (
          <div className="space-y-3">
            {runs.map((r) => (
              <div key={`${r.slot}-${r.period}`} className="rounded-lg border border-line bg-bg-elevated p-3" data-testid={`autopilot-run-${r.slot}-${r.period}`}>
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${
                    r.status === "done" ? "bg-emerald-500/15 text-emerald-300" : r.status === "failed" ? "bg-rose-500/15 text-rose-300" : "bg-sky-500/15 text-sky-300"}`}>
                    {r.status}
                  </span>
                  <span className="font-semibold text-ink-primary">{r.label}</span>
                  {r.category && <span className="text-ink-muted capitalize">{r.category}</span>}
                  {r.voice && <span className="text-ink-muted">· {r.voice}</span>}
                  <span className="ml-auto font-mono text-[10px] text-ink-muted">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                {r.status === "failed" && r.error && <div className="mt-1 text-[11px] text-rose-400">{r.error}</div>}
                {r.audio_url && <audio controls preload="none" className="w-full h-8 mt-2" src={`${API_BASE}/imaging/assets/${r.asset_id}/audio`} />}
                {r.status === "done" && r.script && (
                  <button
                    onClick={() => produceInNewsStudio(navigate, { script: r.script, category: r.category, title: r.label })}
                    data-testid={`autopilot-produce-${r.slot}-${r.period}`}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-3 py-1.5 text-[11px] font-semibold text-brand-amber hover:bg-brand-amber/20 transition"
                  >
                    Produce in News Studio <ArrowUpRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
